import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faPhone, faUserMd, faCalendarCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import './AfterHours.css';

const AfterHours = () => {

  const [animatedElements, setAnimatedElements] = useState(new Set());
  const consultationRef = useRef(null);
  const billingRef = useRef(null);
  const eligibilityRef = useRef(null);
  const formRef = useRef(null);
  const emergencyRef = useRef(null);

  useEffect(() => {
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const elementId = entry.target.id;
          setAnimatedElements(prev => new Set([...prev, elementId]));
          
          if (elementId === 'consultation') {
            // Handle consultation cards - pulse animation
            const consultationCards = entry.target.querySelectorAll('.consultation-card');
            consultationCards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-pulse');
              }, index * 200); // Staggered pulse animation
            });
          } else if (elementId === 'billing') {
            // Handle bulk billing card - shimmer effect
            const billingCard = entry.target.querySelector('.bulk-billing-card');
            if (billingCard) {
              billingCard.classList.add('animate-fade-up');
            }
          } else if (elementId === 'eligibility') {
            // Handle eligibility cards - scale animation
            const eligibilityCards = entry.target.querySelectorAll('.eligibility-card');
            eligibilityCards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-scale');
              }, index * 150); // Staggered scale animation
            });
          } else if (elementId === 'form') {
            // Handle consultation section - scale animation
            const consultationSection = entry.target.querySelector('.form-container');
            if (consultationSection) {
              consultationSection.classList.add('animate-scale');
            }
          } else if (elementId === 'emergency') {
            // Handle emergency card - pulse animation
            const emergencyCard = entry.target.querySelector('.emergency-card');
            if (emergencyCard) {
              emergencyCard.classList.add('animate-pulse');
            }
          }
        }
      });
    }, observerOptions);

    // Observe sections
    const sections = [consultationRef.current, billingRef.current, eligibilityRef.current, formRef.current, emergencyRef.current];
    sections.forEach(section => {
      if (section) observer.observe(section);
    });
    
    return () => {
      observer.disconnect();
    };
  }, []);


  return (
    <div className="after-hours">
      {/* Hero Section */}
      <section className="after-hours-hero">
        <div className="hero-overlay">
          <div className="container">
            <div className="hero-content">
              <h1>After-Hours Care</h1>
              <p>Quality General Practitioner when you need it most - available outside regular clinic hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="after-hours-content">
        <div className="container">
          <div className="content-grid">
            {/* Service Information */}
            <div className="service-info">
              <h2>Emergency & After-Hours Care</h2>
              <p className="service-description">
              Tea Gardens patients can seek advice from Lena Medical Telehealth (lenamedical.com.au) when our regular clinic is closed. Our experienced on-call doctors are available to provide quality medical care for urgent health concerns that cannot wait until the next business day.
              For emergency care please call 000.
              </p>

              {/* Consultation Types */}
              <div className="consultation-types" ref={consultationRef} id="consultation">
                <h3>Available Consultation Options</h3>
                <div className="consultation-cards">
                  <div className="consultation-card">
                    <a 
                      href="https://www.lenamedical.com.au/" 
                      target="" 
                      rel="noopener noreferrer"
                      className="telehealth-link"
                    >
                      <FontAwesomeIcon icon={faPhone} className="consultation-icon" />
                    </a>
                    <h4>Telehealth</h4>
                    <p>Phone consultation with our on-call doctor</p>
                  </div>
                  <div className="consultation-card">
                    <FontAwesomeIcon icon={faUserMd} className="consultation-icon" />
                    <h4>In-Person</h4>
                    <p>Face-to-face consultation at our clinic (when available)</p>
                  </div>
                </div>
              </div>

              {/* Bulk Billing Information */}
              <div className="bulk-billing-info" ref={billingRef} id="billing">
                <h3></h3>
                <div className="bulk-billing-card">
                  <div className="billing-header">
                    <FontAwesomeIcon icon={faClock} className="billing-icon" />
                    <h4>Medicare Bulk Billing Available</h4>
                  </div>
                  <div className="billing-details">
                    <span className="billing-status">No Out-of-Pocket Costs</span>
                    <span className="billing-note">for eligible patients</span>
                  </div>
                  <p className="billing-disclaimer">
                    * Bulk billing available for Medicare card holders and eligible patients
                  </p>
                </div>
              </div>

              {/* Eligibility Information */}
              <div className="eligibility-info" ref={eligibilityRef} id="eligibility">
                <h3>Bulk Billing Eligibility</h3>
                <div className="eligibility-cards">
                  <div className="eligibility-card">
                    <h4>✓ Medicare Card Holders</h4>
                    <p>All patients with valid Medicare cards</p>
                  </div>
                  <div className="eligibility-card">
                    <h4>✓ Concession Card Holders</h4>
                    <p>Healthcare card, pension card holders</p>
                  </div>
                  <div className="eligibility-card">
                    <h4>✓ Children Under 16</h4>
                    <p>All children with Medicare coverage</p>
                  </div>
                  <div className="eligibility-card">
                    <h4>✓ DVA Card Holders</h4>
                    <p>Department of Veterans' Affairs patients</p>
                  </div>
                </div>
              </div>

              {/* After-Hours Consultation */}
              <div className="booking-form-section" ref={formRef} id="form">
                <div className="form-container">
                  <h3>
                    <FontAwesomeIcon icon={faCalendarCheck} className="form-icon" />
                    Request After-Hours Consultation
                  </h3>
                  <p className="consultation-description">
                    For after-hours medical consultations when our clinic is closed, we partner with Lena Medical to provide quality telehealth services.
                  </p>
                  <p className="consultation-info">
                    Their experienced doctors are available 24/7 to help with urgent health concerns that cannot wait until our next business day.
                  </p>
                  <p className="consultation-note">
                    Simply visit their website to book your consultation and receive immediate medical attention from qualified General practice professionals.
                  </p>
                  <a 
                    href="https://www.lenamedical.com.au/" 
                    target="" 
                    rel="noopener noreferrer" 
                    className="lena-medical-btn"
                  >
                    <FontAwesomeIcon icon={faCalendarCheck} />
                    Book with Lena Medical
                  </a>
                </div>
              </div>

              {/* Important Information */}
              <div className="important-info">
                <h3>
                  <FontAwesomeIcon icon={faExclamationTriangle} className="warning-icon" />
                  Important Information
                </h3>
                <ul>
                  <li>After-hours services are for urgent medical concerns.</li>
                  <li>For life-threatening emergencies, call 000 immediately.</li>
                  <li>Our on-call doctor will contact you after booking confirmation.</li>
                  <li>Payment is required at the time of consultation.</li>
                  <li>Medicare rebates may apply for eligible patients.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="emergency-contact" ref={emergencyRef} id="emergency">
        <div className="container">
          <div className="emergency-card">
            <h3>For Life-Threatening Emergencies</h3>
            <p>Call <strong>000</strong> immediately or visit your nearest emergency department</p>
            <div className="emergency-services">
              <div className="service">
                <strong>Ambulance:</strong><br />000
              </div>
              <div className="service">
                <strong>Poison Information:</strong><br />13 11 26
              </div>
              <div className="service">
                <strong>Lifeline:</strong><br />13 11 14
              </div>
              <div className="service">
                <strong>Mental Health Crisis:</strong><br />1800 011 511
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AfterHours;
